import { useInventory, useStore, useUpdateInventoryStatus } from '@gozoki/api'
import type { UniqueArticle } from '@gozoki/api-types'
import { frenchFormat } from '@gozoki/tools'
import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import z from 'zod/v3'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { Can } from '~/utils/ability'
import Page from '../../../components/Page'
import { useToast } from '../../../components/communication/Toast'
import { InventoryDetails } from '../../../components/routes/stores/inventories/InventoryDetails'
import { InventoryGrid } from '../../../components/routes/stores/inventories/InventoryGrid'
import { DashboardCard } from '../../../utils/dashboard/dashboardCards'
import { downloadDataXlsx } from '../../../utils/excel/downloadDataXlsx'
import { useInventoryProductRows } from '../../../utils/hooks/useInventoryProductRows'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/stores/$storeId/inventories/$inventoryId')({
  component: () => (
    <Can I="read" a="stores" passThrough>
      {({ isAllowed }) => (isAllowed ? <InventoryDetailsPage /> : <RbacAccessDeniedPage />)}
    </Can>
  ),
  params: {
    parse: z.object({ inventoryId: z.coerce.number(), storeId: z.coerce.number() }).parse,
  },
})

const CANCEL_BUTTON_STATE = {
  canceled: null,
  pending: { text: "Annuler l'inventaire", disabled: false },
  validating: null,
  validated: null,
}

function InventoryDetailsPage() {
  const navigate = Route.useNavigate()
  const { storeId, inventoryId } = Route.useParams()

  const { data: inventoryDetail, isLoading, refetch: fetchInventory } = useInventory(inventoryId)

  const [updatedInventoryDetail, setUpdatedInventoryDetail] = useState<boolean>(false)
  const { data: store } = useStore(storeId)
  const { mutate: updateInventoryStatus } = useUpdateInventoryStatus()
  const [Toast, showToast] = useToast()

  const dateDateTime = useMemo(
    () => DateTime.fromISO(inventoryDetail?.createdAt ?? ''),
    [inventoryDetail?.createdAt],
  )

  const productRows = useInventoryProductRows(inventoryDetail)

  const unScannedArticles = useMemo(() => {
    if (!inventoryDetail?.currentStock) {
      return []
    }
    return inventoryDetail.currentStock.filter(
      (article) =>
        !inventoryDetail.incomingStock.some(
          (newArticle) => article.checkpointArticleRef === newArticle.checkpointArticleRef,
        ),
    )
  }, [inventoryDetail])

  const expiredArticles = useMemo(() => {
    if (!inventoryDetail?.currentStock) {
      return []
    }
    return inventoryDetail.currentStock.filter((article) => {
      const inventoryDate = new Date(inventoryDetail.createdAt)
      const articleDlcDate = new Date(article.dlc)
      return articleDlcDate < inventoryDate
    })
  }, [inventoryDetail])

  const lostArticles = useMemo(() => {
    if (!unScannedArticles || unScannedArticles.length === 0) {
      return [] as UniqueArticle[]
    }

    return unScannedArticles.filter(
      (article) =>
        !expiredArticles.some(
          (expiredArticle) => expiredArticle.checkpointArticleRef === article.checkpointArticleRef,
        ),
    )
  }, [expiredArticles, unScannedArticles])

  const unScannableProductsCount = useMemo(() => {
    if (!inventoryDetail?.unScannableProducts?.unScannableProducts) {
      return 0
    }
    return inventoryDetail.unScannableProducts.unScannableProducts.reduce(
      (acc, p) => acc + p.count,
      0,
    )
  }, [inventoryDetail])

  const navigateBack = () =>
    navigate({
      params: { storeId },
      search: { tab: 'inventories' },
      to: '/stores/$storeId',
    })

  const handleCancel = useCallback(
    () =>
      updateInventoryStatus(
        { id: inventoryId, status: 'canceled' },
        {
          onError: (error) => {
            showToast({
              message:
                getErrorMessageWithSentry(error) ||
                "Impossible d'annuler l'inventaire. Réessayez plus tard.",
              severity: 'error',
            })
          },
          onSuccess: () => {
            fetchInventory()
            setUpdatedInventoryDetail(!updatedInventoryDetail)
          },
        },
      ),
    [
      inventoryId,
      updatedInventoryDetail,
      fetchInventory,
      setUpdatedInventoryDetail,
      updateInventoryStatus,
      showToast,
    ],
  )

  const handleExportInventory = useCallback(() => {
    const exportData = productRows.map((row) => ({
      delta: row.delta,
      id: row.id,
      inventoryCount: row.inventoryCount,
      label: row.label,
      price: row.price,
      reference: row.reference,
      theoricalCount: row.theoricalCount,
      unscannedCount: row.unscannedCount,
    }))

    const columnWidths: number[] = [10, 20, 35, 15, 30, 12, 20, 10]
    const metadata = {
      date: dateDateTime.toFormat('dd/MM/yyyy'),
      description: inventoryDetail?.comment ?? "Détails de l'inventaire",
      title: `Inventaire du ${dateDateTime.toFormat('dd/MM/yyyy')}`,
    }

    downloadDataXlsx({
      columnWidths,
      columns: {
        id: 'ID',
        reference: 'Référence',
        label: 'Libellé',
        price: "Prix d'achat",
        theoricalCount: 'Stock théorique (avant livraison)',
        inventoryCount: 'Stock relevé',
        unscannedCount: 'Stock non scanné',
        delta: 'Delta',
      },
      data: exportData,
      label: `details_inventaire_${dateDateTime.toFormat('yyyy-MM-dd')}_${(
        store?.label ?? ''
      ).replaceAll(' ', '-')}`,
      metadata,
    })
  }, [inventoryDetail, productRows, store?.label, dateDateTime])

  // Calculate delivered today items with fallback logic
  const deliveredTodayArticles = useMemo(
    () => inventoryDetail?.deliveredArticles ?? [],
    [inventoryDetail],
  )

  const receptionRows = useMemo(() => {
    // Group by product and count volumes
    const productMap = new Map<
      number,
      { id: number; reference: string; label: string; volume: number }
    >()

    deliveredTodayArticles.forEach((article) => {
      const existing = productMap.get(article.product.id)
      if (existing) {
        existing.volume += 1
      } else {
        productMap.set(article.product.id, {
          id: article.product.id,
          label: article.product.label,
          reference: article.product.reference,
          volume: 1,
        })
      }
    })

    return [...productMap.values()]
  }, [deliveredTodayArticles])

  const handleExportReception = useCallback(
    () =>
      downloadDataXlsx({
        columns: {
          id: 'ID',
          reference: 'Référence',
          label: 'Libellé',
          volume: 'Volume reçu',
        },
        data: receptionRows,
        label: `reception-${inventoryDetail?.label ?? 'inventaire'}`,
      }),
    [inventoryDetail, receptionRows],
  )

  const handleExportInventoryTable = useCallback(() => {
    const exportData = productRows.map((row) => ({
      delta: row.delta,
      inventoryCount: row.inventoryCount + row.unscannedCount,
      label: row.label,
      reference: row.reference,
      theoricalCount: row.theoricalCount,
    }))

    downloadDataXlsx({
      columnWidths: [15, 30, 35, 15, 12],
      columns: {
        reference: 'Référence',
        label: 'Libellé',
        theoricalCount: 'Stock théorique',
        inventoryCount: 'Stock relevé',
        delta: 'Delta',
      },
      data: exportData,
      label: `inventaire_${DateTime.fromISO(inventoryDetail?.createdAt ?? '').toFormat(
        'yyyy-MM-dd',
      )}_${(store?.label ?? '').replaceAll(' ', '-')}`,
      metadata: {
        title: `Inventaire du ${dateDateTime.toFormat('dd/MM/yyyy')}`,
        date: dateDateTime.toFormat('dd/MM/yyyy'),
        description:
          inventoryDetail?.comment ?? 'Comparaison entre le stock théorique et le stock relevé',
      },
    })
  }, [inventoryDetail, productRows, store?.label, dateDateTime])

  if (!inventoryDetail) {
    return null
  }

  return (
    <Page>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: 10,
          paddingBottom: 40,
          paddingLeft: 20,
        }}
      >
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={fonts.pageSubtitle} flexGrow={1}>
          {store?.label} / {inventoryDetail.label}
        </Typography>
        <Box display="flex" gap={2}>
          {CANCEL_BUTTON_STATE[inventoryDetail.status] ? (
            <Button
              variant="outlined"
              color="error"
              sx={{ bgcolor: 'white', height: '36px' }}
              onClick={handleCancel}
            >
              {CANCEL_BUTTON_STATE[inventoryDetail.status]?.text}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="success"
              sx={{ height: '36px' }}
              onClick={handleExportInventory}
            >
              Exporter inventaire
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            sx={{ height: '36px' }}
            onClick={handleExportInventoryTable}
          >
            Exporter tableau
          </Button>
          {inventoryDetail.status === 'validated' && (
            <Button
              variant="outlined"
              color="success"
              sx={{ height: '36px' }}
              onClick={handleExportReception}
            >
              Exporter réception
            </Button>
          )}
          {inventoryDetail.status === 'canceled' && (
            <Button variant="contained" disabled sx={{ height: '36px' }}>
              Annulé
            </Button>
          )}
        </Box>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          paddingBottom: 20,
          ...fonts.roboto,
        }}
      >
        <DashboardCard
          label="Total stock"
          bigValue={`${frenchFormat(inventoryDetail.currentStock.length)}`}
          orange
          smallValue="Stock théorique avant inventaire"
        />
        <DashboardCard
          label="Total scannés"
          bigValue={`${frenchFormat(inventoryDetail.incomingStock.length)}`}
          orange
          smallValue="Articles scannés durant l'inventaire"
        />
        <DashboardCard
          label="Total vol"
          bigValue={`${frenchFormat(lostArticles.length)}`}
          smallValue="Articles volés depuis le dernier inventaire"
        />
        <DashboardCard
          label="Total casse"
          bigValue={`${frenchFormat(expiredArticles.length)}`}
          smallValue="Articles expirés ou détruits depuis le dernier inventaire"
        />
        <DashboardCard
          label="Total livraison"
          bigValue={`${frenchFormat(deliveredTodayArticles.length)}`}
          smallValue="Articles livrés depuis le dernier inventaire"
        />
        <DashboardCard
          label="Total non scannables"
          bigValue={`${frenchFormat(unScannableProductsCount)}`}
          smallValue="Articles en boutique mais non scannables, à inclure dans la casse"
        />
      </div>
      <div style={{ flex: 1 }}>
        <InventoryDetails
          label={inventoryDetail.label || ''}
          comment={inventoryDetail.comment || ''}
          inventoryId={inventoryDetail.id}
          onValidate={() => fetchInventory()}
        />
        <Paper>
          <InventoryGrid
            isLoading={isLoading}
            productRows={productRows}
            inventoryStatus={inventoryDetail.status}
          />
        </Paper>
      </div>
      <Toast />
    </Page>
  )
}
